import clsx from 'clsx';
import * as React from 'react';
import { useRecoilState } from 'recoil';

import {
    BLOCK_IMAGE,
    INLINE_IMAGE,
    INLINE_LINK,
    type InlineImageData,
    type InlineImageSize,
    images,
} from '@gitbook/doc-core';
import { Popover, PortalRelative, type PortalRelativeRef } from '@gitbook/react-uikit';
import type { Inline } from '@gitbook/slate';
import type { InlineProps } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    DialogRoot,
    DialogTrigger,
    LoadingIndicator,
    ProgressiveImage,
    useDialogTriggerState,
} from '@gitbook/spine-react';
import { useHead } from '@gitbook/util-react-head';
import { useSafeLayoutEffect } from '@gitbook/util-react-hooks';

import { useImageContentReference } from '../../ContentReferences';
import { useMediaGallery } from '../../MediaGallery';
import { useIsNodeEstimatedInViewport, VoidInlineFrame } from '../common';
import { ContentEditorContext } from '../contexts';
import { EditAltTextDialog } from './EditAltTextModal';
import { ReplaceImageButton } from './ReplaceImageButton';
import { imageDimensionsAtom } from './state';

type InlineImageRef = {
    openInGallery: () => void;
};

/**
 * Component to render an inline image in the document.
 */
export function InlineImage(props: InlineProps) {
    const { editor, node, ancestors } = props;

    const withPriorityLoading = useIsNodeEstimatedInViewport(editor, node.key);
    const inlineImageRef = React.useRef<InlineImageRef>(null);
    const size: InlineImageSize = node.data.get('size', 'original');
    const isWrappedByLink = ancestors.some(
        (ancestor) => ancestor.object === 'inline' && ancestor.type === INLINE_LINK
    );

    const anchor = (anchorProps: { ref?: React.Ref<HTMLSpanElement>; onClick?: () => void }) => (
        <VoidInlineFrame
            ref={anchorProps.ref}
            onClick={anchorProps.onClick}
            className="align-middle"
            {...props}
        >
            <React.Suspense fallback={<LoadingIndicator size="small" />}>
                <SizedInlineImage
                    node={node}
                    size={size}
                    ref={inlineImageRef}
                    withPriorityLoading={withPriorityLoading}
                />
            </React.Suspense>
        </VoidInlineFrame>
    );

    if (editor.readOnly) {
        return anchor({
            // Do not open the image preview in the gallery when it is wrapped by a link
            // and the link click through.
            onClick: isWrappedByLink
                ? () => {
                      inlineImageRef.current?.openInGallery();
                  }
                : undefined,
        });
    }

    return <EditImageWrapper {...props} anchor={anchor} />;
}

/** Render an inline image with the right size */
const SizedInlineImage = React.forwardRef(function SizedInlineImageWithRef(
    props: {
        node: Inline;
        size: InlineImageSize;
        withPriorityLoading: boolean;
    },
    ref: React.Ref<InlineImageRef>
) {
    const { node, size, withPriorityLoading } = props;

    const { ref: contentRef, refDark: contentRefDark }: InlineImageData =
        node.data.toFrozenObject();

    const imageAltText = images.getImageAltText(node);
    const source = useImageContentReference(contentRef, contentRefDark);
    const gallery = useMediaGallery();
    const url = source?.uri;

    const [imageDimensionsState, setImageDimensionsState] = useRecoilState(imageDimensionsAtom);
    const [complete, setComplete] = React.useState(false);
    const [error, setError] = React.useState(false);

    useHead((head) => {
        if (!withPriorityLoading || !source?.uri) {
            return;
        }

        head.writeAsync({
            type: 'link',
            rel: 'preload',
            href: source.uri,
            as: 'image',
        });
    });

    React.useImperativeHandle(
        ref,
        () => ({
            openInGallery: () => {
                if (url) {
                    gallery.open([{ url }]);
                }
            },
        }),
        [url, gallery]
    );

    let finalSize = {};

    if (size === 'original') {
        finalSize = {
            maxWidth: 300,
        };
    }

    const errorFallback = <BrokenInlineImage />;

    if (!source || error) {
        return errorFallback;
    }

    return (
        <ProgressiveImage
            onLoad={(e) => {
                setComplete((e.target as HTMLImageElement).complete);
                const { naturalWidth, naturalHeight } = e.target as HTMLImageElement;
                if (!naturalWidth || !naturalHeight) {
                    return;
                }
                setImageDimensionsState((state) => ({
                    ...state,
                    [node.key]: {
                        width: naturalWidth,
                        height: naturalHeight,
                    },
                }));
            }}
            onError={(_) => {
                setError(true);
            }}
            alt={imageAltText}
            width={source.width ?? imageDimensionsState[node.key]?.width}
            height={source.height ?? imageDimensionsState[node.key]?.height}
            src={source.uri}
            resize={false}
            rounded={false}
            loading={withPriorityLoading ? 'eager' : 'lazy'}
            style={finalSize}
            className={clsx(
                size === 'line' ? 'h-[1lh] max-h-[1lh] w-auto' : 'h-auto',
                complete && size === 'original' && 'w-full'
            )}
        />
    );
});

/** Fallback when the image can't load. */
function BrokenInlineImage() {
    return (
        <div className="flex rounded bg-muted p-1">
            <Icon.CloudOff size={18} className="text-muted" />
        </div>
    );
}

/** Wrapper with popover when editing inline images */
function EditImageWrapper(
    props: InlineProps & {
        anchor: (anchorProps: {
            ref?: React.Ref<HTMLElement>;
            onClick?: () => void;
        }) => React.ReactElement;
    }
) {
    const { node, anchor, isFocused, editor } = props;
    const { logAnalyticsEvent } = React.useContext(ContentEditorContext);

    const portalRef = React.useRef<PortalRelativeRef>(null);

    // Only show the menu when only the inline void is selected.
    const isOnlySelected = isFocused && editor.value.selection.isCollapsed;

    // To handle press in the popover, we need to keep displaying it when the user starts clicking on it
    // and the focus is moved outside the editor.
    const [wasVisible, setWasVisible] = React.useState(false);
    React.useEffect(() => {
        if (isOnlySelected) {
            setWasVisible(true);
            return;
        }

        const timeout = setTimeout(() => {
            setWasVisible(false);
        }, 300);

        return () => {
            clearTimeout(timeout);
        };
    }, [isOnlySelected]);

    /** Select the image */
    const onClick = () => {
        editor.change((change) => {
            change.collapseToStartOf(node).focus();
        });
    };

    /** Change size for the image */
    const onChangeSize = (size: InlineImageSize) => {
        editor.change((change) => {
            change.insertDataByKey(node.key, ['size'], size);
            change.collapseToStartOf(node).focus();
        });
    };

    /** Replace the inline image by a block */
    const onReplaceWithBlock = () => {
        logAnalyticsEvent({
            type: 'edit_page_document_edit_block_type',
            fromType: INLINE_IMAGE,
            toType: BLOCK_IMAGE,
        });
        editor.change((change) => {
            images.replaceInlineImageWithBlock(change, node);
        });
    };

    // Reposition the portal when the size changes
    // It's a noop when the portal is not opened.
    useSafeLayoutEffect(() => {
        portalRef.current?.updatePosition();
    }, [node.data]);

    const altTextDialogTrigger = useDialogTriggerState();

    return (
        <>
            <DialogRoot {...altTextDialogTrigger.rootProps}>
                <EditAltTextDialog
                    defaultValue={images.getImageAltText(node) || ''}
                    onChange={(value) => {
                        editor.change((change) => {
                            change.insertDataByKey(node.key, ['alt'], value);
                        });
                    }}
                />
            </DialogRoot>
            <PortalRelative
                ref={portalRef}
                opened={isOnlySelected || wasVisible}
                anchor={({ ref }) =>
                    anchor({
                        ref: ref as React.Ref<HTMLElement>,
                        onClick,
                    })
                }
            >
                <Popover kind="tooltip">
                    <div className="flex items-center gap-2 rounded border bg-base p-1">
                        <Button
                            kind="blank"
                            size="small"
                            label="Inline size"
                            onPress={() => onChangeSize('line')}
                            leadingIcon={Icon.ImageSizeSmall}
                            iconOnly
                        />
                        <Button
                            kind="blank"
                            size="small"
                            label="Original size"
                            onPress={() => onChangeSize('original')}
                            leadingIcon={Icon.ImageSizeLarge}
                            iconOnly
                        />
                        <Button
                            kind="blank"
                            size="small"
                            label="Convert to block"
                            onPress={onReplaceWithBlock}
                            leadingIcon={Icon.ImageSizeBlock}
                            iconOnly
                        />

                        <ReplaceImageButton size="small" editor={editor} node={node} />

                        <DialogTrigger trigger={altTextDialogTrigger}>
                            <Button
                                kind="blank"
                                size="small"
                                label="ALT text"
                                leadingIcon={Icon.AltText}
                                iconOnly
                            />
                        </DialogTrigger>
                    </div>
                </Popover>
            </PortalRelative>
        </>
    );
}
